import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { StyleSheet, View, ScrollView } from 'react-native';
import { capitalize, pick } from 'lodash';
import moment from 'moment';
import Markdown from 'react-native-markdown-display';
import table from 'markdown-table';

import IconButton from '../shared/IconButton';
import Text from '../shared/Text';
import Image from '../shared/Image';
import Modal from '../shared/Modal';

import heroStore, { increaseParameter, increaseAbility, increaseSkill } from '../../stores/hero';
import appStore from '../../stores/app';

import { t, getLocalValue, getLngKey } from '../../utils/appUtils';

import { FULL_WIDTH as BODY_WIDTH } from './Body';

import { PADDING, PADDING1, WINDOW_WIDTH, MARGIN_10, pOrT } from '../styleConstants';

import logImport from '../../lib/log';
const log: any = logImport;

import { WarriorType } from '../../lib/types';
import { getByValue } from '../../utils/utils';
import { IS_WEB } from '../../lib/appConstants';

const INFO_WIDTH = WINDOW_WIDTH - PADDING * 3 - BODY_WIDTH;
const WIDTH = INFO_WIDTH - PADDING * 2;
const MIN_WIDTH = pOrT(165, 198);
const MAX_BLOCK_WIDTH = 250;
let BLOCK_WIDTH = pOrT(MIN_WIDTH, WIDTH / 3);
BLOCK_WIDTH = Math.min(BLOCK_WIDTH, MAX_BLOCK_WIDTH);
BLOCK_WIDTH = Math.max(BLOCK_WIDTH, MIN_WIDTH);

const TOUCHABLE = pOrT(18, 30);

// const MODIFIERS_HEIGHT_PHONE = 135;
// const DAMAGE_PROTECTION_HEIGHT_PHONE = 160;

// const WRAP_TO_VERTICAL_SCROLL =
//   WINDOW_HEIGHT -
//     HP_HEIGHT -
//     MODIFIERS_HEIGHT_PHONE -
//     DAMAGE_PROTECTION_HEIGHT_PHONE -
//     PADDING * 3 -
//     PADDING1 <
//   0;

// const WRAP_TO_HORIZONTAL_SCROLL =
//   INFO_WIDTH - BLOCK_WIDTH * 3 - PADDING * 2 < 0;

const getInfo = ({ skills, tableExperience }: { skills: any; tableExperience: any }) => {
  const tableExperienceKeys = [
    'level',
    'experience',
    'numberOfParameters',
    'numberOfAbilities',
    'numberOfSkills',
    'money',
  ];
  return {
    SKILLS: table(
      [['name', 'features'].map((item: string) => t(`hero.infoObj.skills.${item}`))].concat(
        skills.map((item: any) => {
          const value = {
            name: getLocalValue(item),
            features: item.features
              .map((iitem: any) => `${t(`hero.${iitem.name}`)} +${iitem.plus}`)
              .join('&#10;'),
          };
          return Object.values(value);
        }),
      ),
    ),
    TABLE_EXPERIENCE: table(
      [tableExperienceKeys.map((item: string) => t(`hero.infoObj.tableExperience.${item}`))].concat(
        tableExperience.map((item: any) => {
          return Object.values(pick(item, tableExperienceKeys));
        }),
      ),
    ),
  };
};

const styles = StyleSheet.create({
  infoBlock: {
    width: BLOCK_WIDTH,
    padding: PADDING1,
    position: 'relative',
  },
  title: {
    fontSize: pOrT(14, 16),
    marginBottom: 3,
  },
  itemText: {},
  secondItemWrapper: {
    marginTop: PADDING,
  },
  actionsTextWrapper: {
    flexDirection: 'row',
    marginTop: PADDING1,
  },
});

const getItemWrapperStyle = (index?: number) => ({
  marginLeft: -PADDING1,
  paddingLeft: PADDING1,
  marginRight: -PADDING1,
  // backgroundColor: !(index % 2) ? GOLDEN100 : undefined,
});

const iconSize: any = pOrT({ width: 16, height: 16 }, { width: 19, height: 19 });

function getFeatureParam(orig: any, feature: any): string {
  let output = feature;
  if (orig === feature) {
    return `${output}`;
  }

  output += ` (${orig}`;

  if (feature > orig) {
    output += '+';
  }

  output += feature - orig;

  output += ')';
  return output;
}

let increasing = false;
async function increaseWrapper(fn: () => Promise<void>): Promise<void> {
  if (increasing) return;
  increasing = true;
  try {
    await fn();
  } catch (e) {} // eslint-disable-line
  increasing = false;
}

export const InfoIcon = ({
  onPress,
  iconStyle,
  style,
}: {
  iconStyle?: any;
  onPress: () => void;
  style: any;
}) => {
  return (
    <IconButton
      onPress={onPress}
      hitSlop={{
        right: 0,
        top: 0,
        bottom: 0,
        left: 18,
      }}
      style={{
        ...style,
      }}
      iconStyle={
        iconStyle || {
          width: pOrT(14, 16),
          height: pOrT(14, 16),
        }
      }
      size={14}
      name="info"
      source={require('../../assets/images/info.png')}
    />
  );
};

const SkillsInfo = observer((_props?: { hero?: WarriorType }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { hero } = heroStore;
  const { skills } = appStore.initData;

  const [page, setPage] = useState(0);

  const perPage = 5;
  return (
    <View>
      <Image
        style={{
          width: BLOCK_WIDTH,
          height: pOrT(hero.numberOfSkills ? 148 : 130, hero.numberOfSkills ? 180 : 150),
          position: 'absolute',
        }}
        resizeMode="stretch"
        source={require('../../assets/images/paper2.png')}
      />
      <View
        style={[
          styles.infoBlock,
          {
            height: pOrT(hero.numberOfSkills ? 148 : 130, hero.numberOfSkills ? 180 : 150),
          },
        ]}
      >
        <View style={{ flexDirection: 'row' }}>
          <Text bold style={styles.title}>
            {t('hero.skills')}
          </Text>
          <InfoIcon
            style={{ marginLeft: 2, marginTop: 2 }}
            onPress={() => {
              setShowInfo(true);
            }}
          />
        </View>
        {skills.slice(page * perPage, page * perPage + perPage).map((skill: any) => {
          const heroSkill = getByValue(hero.skills, skill.id, 'skill');
          return (
            <View key={`${skill.id}`} style={{ flexDirection: 'row', marginTop: 2 }}>
              <Text style={[styles.itemText, { width: pOrT(100, 125) }]}>
                {getLocalValue(skill)}
              </Text>
              <Text bold style={styles.itemText}>
                {heroSkill ? heroSkill.level : 0}
              </Text>
              {hero.numberOfSkills ? (
                <IconButton
                  onPress={() => {
                    increaseWrapper(async () => {
                      log('increaseSkillAction', {
                        warriorLogin: heroStore.hero.login,
                        name: skill.name,
                      });
                      await increaseSkill(skill.id);
                    });
                  }}
                  style={{ marginLeft: 8 }}
                  hitSlop={{
                    top: 0,
                    bottom: 0,
                    left: TOUCHABLE,
                    right: TOUCHABLE,
                  }}
                  iconStyle={iconSize}
                  size={iconSize}
                  name="plus"
                  title={t('add')}
                  source={require('../../assets/images/addIcon.png')}
                />
              ) : null}
            </View>
          );
        })}
        {hero.numberOfSkills ? (
          <View style={styles.actionsTextWrapper}>
            <Text bold style={styles.itemText}>
              {t('hero.toIncrease')}
            </Text>
            <Text bold style={[styles.itemText, { marginLeft: 10 }]}>
              {hero.numberOfSkills}
            </Text>
          </View>
        ) : null}
        {page > 0 ? (
          <IconButton
            onPress={() => {
              setPage(page - 1);
            }}
            style={{
              position: 'absolute',
              top: 8,
              right: 25,
            }}
            size={iconSize}
            iconStyle={iconSize}
            name="left"
            title={t('previous')}
            source={require('../../assets/images/leftIcon.png')}
          />
        ) : null}
        {(page + 1) * perPage < skills.length ? (
          <IconButton
            onPress={() => {
              setPage(page + 1);
            }}
            style={{
              position: 'absolute',
              top: 8,
              right: 10,
            }}
            size={iconSize}
            iconStyle={iconSize}
            name="right"
            title={t('next')}
            source={require('../../assets/images/rightIcon.png')}
          />
        ) : null}
      </View>
      {showInfo ? (
        <Modal
          size={{
            width: pOrT(467, 750),
            height: pOrT(295, 420),
          }}
          title={t('hero.skills')}
          onHide={() => {
            setShowInfo(false);
          }}
          marginTop={IS_WEB ? 100 : undefined}
          hideOverlay
        >
          <ScrollView style={{ marginTop: pOrT(25, 45), marginHorizontal: 20, marginBottom: 20 }}>
            <Markdown>{getInfo(appStore.initData).SKILLS}</Markdown>
          </ScrollView>
        </Modal>
      ) : null}
    </View>
  );
});

export const ParametersInfo = observer(
  ({ warrior, isInfo }: { warrior: WarriorType; isInfo?: boolean }) => {
    const canIncrease = Boolean(!isInfo && warrior.numberOfParameters);
    const height = pOrT(canIncrease ? 130 : 110, canIncrease ? 160 : 135);

    return (
      <View>
        <Image
          style={{
            width: BLOCK_WIDTH,
            height,
            position: 'absolute',
          }}
          resizeMode="stretch"
          source={require('../../assets/images/paper2.png')}
        />
        <View style={[styles.infoBlock, { height }, isInfo && { width: BLOCK_WIDTH }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text bold style={styles.title}>
              {t('hero.parameters')}
            </Text>
          </View>
          {['strength', 'dexterity', 'intuition', 'health'].map((item: string, index: number) => (
            <View
              key={`${item}`}
              style={[
                getItemWrapperStyle(index),
                {
                  flexDirection: 'row',
                  marginTop: 2,
                },
              ]}
            >
              <Text style={[styles.itemText, { width: pOrT(60, 75) }]}>{t(`hero.${item}`)}</Text>
              <Text bold style={styles.itemText}>
                {getFeatureParam((warrior as any)[item], (warrior.feature as any)[item])}
              </Text>
              {canIncrease ? (
                <IconButton
                  onPress={() => {
                    increaseWrapper(async () => {
                      log('increaseParameterAction', {
                        warriorLogin: heroStore.hero.login,
                        name: item,
                      });
                      await increaseParameter(item);
                    });
                  }}
                  style={{ marginLeft: 8 }}
                  hitSlop={{
                    top: 0,
                    bottom: 0,
                    left: TOUCHABLE,
                    right: TOUCHABLE,
                  }}
                  size={iconSize}
                  iconStyle={iconSize}
                  name="plus"
                  testID={'increaseParameterButton_' + item}
                  source={require('../../assets/images/addIcon.png')}
                  title={t('add')}
                />
              ) : null}
            </View>
          ))}
          {canIncrease ? (
            <View style={styles.actionsTextWrapper}>
              <Text bold style={styles.itemText}>
                {t('hero.toIncrease')}
              </Text>
              <Text bold style={[styles.itemText, { marginLeft: MARGIN_10 }]}>
                {warrior.numberOfParameters}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
    );
  },
);

export const MoreInfo = observer(({ warrior }: { warrior: WarriorType }) => {
  return (
    <View>
      <Image
        style={{
          width: BLOCK_WIDTH,
          height: pOrT(180, 205),
          position: 'absolute',
        }}
        resizeMode="stretch"
        source={require('../../assets/images/paper2.png')}
      />
      <View style={styles.infoBlock}>
        <Text bold style={styles.title}>
          More
        </Text>
        {[
          {
            key: 'id',
            label: 'Id',
            value: warrior.id.slice(0, 10),
          },
          {
            key: 'quick',
            label: 'Quick',
            value: String(Boolean(warrior.quick)),
          },
          { key: 'money', label: 'Money', value: warrior.money },
          {
            key: 'created',
            label: 'Created',
            value: moment(warrior.created).format('l'),
          },
          { key: 'experience', label: 'Experience', value: warrior.experience },
          {
            key: 'onlineTime',
            label: 'Online Time',
            value: warrior.onlineTime
              ? moment.utc(warrior.onlineTime * 1000).format('HH:mm:ss')
              : '',
          },
          { key: 'platform', label: 'Platform', value: warrior.platform },
          {
            key: 'combat',
            label: 'Combat',
            value: warrior.combat ? warrior.combat.slice(0, 10) : '',
          },
        ].map((item: any, index: number) => (
          <View
            key={`${item.key}`}
            style={[getItemWrapperStyle(index), { flexDirection: 'row', marginTop: 2 }]}
          >
            <Text style={[styles.itemText, { width: pOrT(80, 95) }]}>{item.label}</Text>
            <Text bold style={styles.itemText}>
              {item.value}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
});

export const GeneralInfo = observer(
  ({ warrior, isInfo }: { warrior: WarriorType; isInfo?: boolean }) => {
    const [showInfo, setShowInfo] = useState(false);
    const tableExperienceItem = appStore.initData.tableExperience.find(
      (item: any) => item.level > warrior.level,
    );

    return (
      <View>
        <Image
          style={{
            width: BLOCK_WIDTH,
            height: pOrT(110, 140),
            position: 'absolute',
          }}
          resizeMode="stretch"
          source={require('../../assets/images/paper2.png')}
        />
        <View style={[styles.infoBlock, isInfo && { width: BLOCK_WIDTH }]}>
          <Text bold style={styles.title}>
            {t('hero.info')}
          </Text>
          {['wins', 'losses', 'draws'].map((item: string, index: number) => (
            <View
              key={`${item}`}
              style={[getItemWrapperStyle(index), { flexDirection: 'row', marginTop: 2 }]}
            >
              <Text style={[styles.itemText, { width: pOrT(80, 95) }]}>{t(`hero.${item}`)}</Text>
              <Text bold style={styles.itemText}>
                {(warrior as any)[`numberOf${capitalize(item)}`]}
              </Text>
            </View>
          ))}
          <View style={[styles.actionsTextWrapper]}>
            <Text bold style={styles.itemText}>
              {t('hero.experienceMin')}
            </Text>
            {!isInfo ? (
              <InfoIcon
                style={{ marginLeft: 2, marginTop: 2 }}
                onPress={() => {
                  setShowInfo(true);
                }}
              />
            ) : null}
            <Text bold style={[styles.itemText, { marginLeft: 10 }]}>
              {warrior.experience}
              {' / '}
              {/* $FlowFixMe */}
              {tableExperienceItem!.experience}
            </Text>
          </View>
        </View>
        {showInfo ? (
          <Modal
            size={{
              width: pOrT(500, 750),
              height: pOrT(295, 520),
            }}
            marginTop={IS_WEB ? 100 : undefined}
            title={t('hero.infoObj.tableExperience.title')}
            onHide={() => {
              setShowInfo(false);
            }}
            hideOverlay
          >
            <ScrollView style={{ marginTop: pOrT(25, 45), marginHorizontal: 20, marginBottom: 20 }}>
              <Markdown>{getInfo(appStore.initData).TABLE_EXPERIENCE}</Markdown>
            </ScrollView>
          </Modal>
        ) : null}
      </View>
    );
  },
);

export const ModifiersInfo = observer(
  ({ warrior, isInfo }: { warrior: WarriorType; isInfo?: boolean }) => (
    <View>
      <Image
        style={{
          width: BLOCK_WIDTH,
          height: pOrT(145, 175),
          position: 'absolute',
        }}
        resizeMode="stretch"
        source={require('../../assets/images/paper2.png')}
      />
      <View
        style={[styles.infoBlock, isInfo && { width: BLOCK_WIDTH }, { height: pOrT(145, 175) }]}
      >
        <Text bold style={styles.title}>
          {t('hero.modifiers')}
        </Text>
        {['devastate', 'dodge', 'accuracy', 'durability', 'blockBreak', 'armorBreak'].map(
          (item: string, index: number) => (
            <View
              key={`${item}`}
              style={[getItemWrapperStyle(index), { flexDirection: 'row', marginTop: 2 }]}
            >
              <Text style={[styles.itemText, { width: pOrT(90, 105) }]}>{t(`hero.${item}`)}</Text>
              <Text bold style={styles.itemText}>{`${(warrior.feature as any)[item]}%`}</Text>
            </View>
          ),
        )}
      </View>
    </View>
  ),
);

export const DamageProtectionInfo = observer(
  ({ warrior, isInfo }: { warrior: WarriorType; isInfo?: boolean }) => (
    <View>
      <Image
        style={{
          width: BLOCK_WIDTH,
          height: pOrT(165, 200),
          position: 'absolute',
        }}
        resizeMode="stretch"
        source={require('../../assets/images/paper2.png')}
      />
      <View style={[styles.infoBlock, isInfo && { width: BLOCK_WIDTH }]}>
        <Text bold style={styles.title}>
          {t('hero.damageNProtection')}
        </Text>
        <View style={{ flexDirection: 'row', marginTop: 2 }}>
          <Text style={[styles.itemText, { width: pOrT(65, 75) }]}>{t('hero.damage')}</Text>
          <Text bold style={styles.itemText}>
            {warrior.feature!.damageMin >= warrior.feature!.damageMax
              ? warrior.feature!.damageMin
              : `${warrior.feature!.damageMin} - ${warrior.feature!.damageMax}`}
          </Text>
        </View>
        <Text bold style={{ marginTop: 5, color: '#110600' }}>
          {t('hero.protection')}
        </Text>
        {['head', 'breast', 'belly', 'groin', 'legs'].map((item: string, index: number) => (
          <View
            key={`${item}`}
            style={[getItemWrapperStyle(index), { flexDirection: 'row', marginTop: 2 }]}
          >
            <Text style={[styles.itemText, { width: pOrT(65, 75) }]}>{t(`hero.${item}`)}</Text>
            <Text bold style={styles.itemText}>
              {(warrior.feature as any)[`protection${capitalize(item)}`]}
            </Text>
          </View>
        ))}
      </View>
    </View>
  ),
);

const AbilitiesInfo = observer(({ hero }: { hero: WarriorType }) => (
  <View>
    <Image
      style={{
        width: BLOCK_WIDTH,
        height: pOrT(hero.numberOfAbilities ? 150 : 130, hero.numberOfAbilities ? 180 : 150),
        position: 'absolute',
      }}
      resizeMode="stretch"
      source={require('../../assets/images/paper2.png')}
    />
    <View style={styles.infoBlock}>
      <Text bold style={styles.title}>
        {t('hero.abilities')}
      </Text>
      {['swords', 'axes', 'knifes', 'clubs', 'shields'].map((item: string, index: number) => (
        <View
          key={`${item}`}
          style={[getItemWrapperStyle(index), { flexDirection: 'row', marginTop: 2 }]}
        >
          <Text style={[styles.itemText, { width: pOrT(60, 75) }]}>{t(`hero.${item}`)}</Text>
          <Text bold style={styles.itemText}>
            {getFeatureParam((hero as any)[item], (hero.feature as any)[item])}
          </Text>
          {hero.numberOfAbilities ? (
            <IconButton
              onPress={() => {
                increaseWrapper(async () => {
                  log('increaseAbilityAction', {
                    warriorLogin: heroStore.hero.login,
                    name: item,
                  });
                  await increaseAbility(item);
                });
              }}
              style={{ marginLeft: 8 }}
              hitSlop={{
                top: 0,
                bottom: 0,
                left: TOUCHABLE,
                right: TOUCHABLE,
              }}
              size={iconSize}
              iconStyle={iconSize}
              name="plus"
              title={t('add')}
              source={require('../../assets/images/addIcon.png')}
            />
          ) : null}
        </View>
      ))}
      {hero.numberOfAbilities ? (
        <View style={styles.actionsTextWrapper}>
          <Text bold style={styles.itemText}>
            {t('hero.toIncrease')}
          </Text>
          <Text bold style={[styles.itemText, { marginLeft: 10 }]}>
            {hero.numberOfAbilities}
          </Text>
        </View>
      ) : null}
    </View>
  </View>
));

// eslint-disable-next-line
const SmallScreenWrapper = ({ children }: { children: any }) => (
  <ScrollView>
    <ScrollView horizontal style={{ width: INFO_WIDTH }}>
      {children}
    </ScrollView>
  </ScrollView>
);

const Info = () => {
  const { hero } = heroStore;

  const WrapperComp = SmallScreenWrapper;

  return (
    <WrapperComp>
      <View>
        <ParametersInfo warrior={hero} />
        <View style={styles.secondItemWrapper}>
          <GeneralInfo warrior={hero} />
        </View>
      </View>
      <View style={{ marginLeft: PADDING }}>
        <ModifiersInfo warrior={hero} />
        <View style={styles.secondItemWrapper}>
          <DamageProtectionInfo warrior={hero} />
        </View>
      </View>
      <View style={{ marginLeft: PADDING }}>
        <SkillsInfo hero={hero} />
        <View style={styles.secondItemWrapper}>
          <AbilitiesInfo hero={hero} />
        </View>
      </View>
    </WrapperComp>
  );
};

Info.displayName = 'Info';

export default Info;
